import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react';
import { dipAPI } from '@/utils/api';

export const DIP_STEPS = [
  { key: 'formula',       label: 'Formule',     route: 'formula',       statusKey: 'has_formula' },
  { key: 'container',     label: 'Contenants',  route: 'container',     statusKey: 'has_containers' },
  { key: 'manufacturing', label: 'Fabrication', route: 'manufacturing', statusKey: 'has_manufacturing' },
  { key: 'toxicology',    label: 'Toxicologie', route: 'toxicology',    statusKey: 'has_toxicology' },
  { key: 'labels',        label: 'Étiquettes',  route: 'labels',        statusKey: 'has_labels' },
  { key: 'certificates',  label: 'Certificats', route: 'certificates',  statusKey: 'has_certificates', optional: true },
  { key: 'dip',           label: 'Générer DIP', route: 'dip',           statusKey: null },
];

const STEPS = DIP_STEPS;

const DipBreadcrumb = ({ productId, productName, currentStep }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState({});

  useEffect(() => {
    if (!productId) return;
    dipAPI.getStatus(productId).then(res => setStatus(res.data)).catch(() => {});
  }, [productId]);

  const goTo = (route) => navigate(`/product/${productId}/${route}`);

  const currentIdx = STEPS.findIndex(s => s.key === currentStep);

  return (
    <div className="mb-8" data-testid="dip-breadcrumb">
      {/* Product name + back */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate('/products')}
          className="text-sm text-muted-foreground hover:text-[#205F79] transition-colors"
          data-testid="breadcrumb-back-products"
        >
          Produits
        </button>
        <ChevronRight size={14} className="text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{productName || '...'}</span>
      </div>

      {/* Step pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        {STEPS.map((step, idx) => {
          const done = step.statusKey ? status[step.statusKey] : false;
          const isActive = step.key === currentStep;
          const isLast = idx === STEPS.length - 1;
          return (
            <React.Fragment key={step.key}>
              <button
                onClick={() => goTo(step.route)}
                data-testid={`breadcrumb-step-${step.key}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all
                  ${isActive
                    ? 'bg-[#205F79] text-white font-medium shadow-sm'
                    : done
                      ? 'bg-green-50 text-green-800 hover:bg-green-100 border border-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}
              >
                {done ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                <span>{step.label}</span>
                {step.optional && !isActive && (
                  <span className="text-[10px] uppercase tracking-wider opacity-70">opt.</span>
                )}
              </button>
              {!isLast && <ChevronRight size={14} className="text-gray-300" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Prev/Next nav */}
      <div className="flex justify-between items-center mt-3">
        <button
          onClick={() => currentIdx > 0 && goTo(STEPS[currentIdx - 1].route)}
          disabled={currentIdx <= 0}
          data-testid="breadcrumb-prev"
          className="text-xs text-[#205F79] hover:underline disabled:opacity-30 disabled:no-underline"
        >
          {currentIdx > 0 ? `← ${STEPS[currentIdx - 1].label}` : ''}
        </button>
        <button
          onClick={() => currentIdx < STEPS.length - 1 && goTo(STEPS[currentIdx + 1].route)}
          disabled={currentIdx >= STEPS.length - 1 || currentIdx < 0}
          data-testid="breadcrumb-next"
          className="text-xs text-[#205F79] hover:underline disabled:opacity-30 disabled:no-underline"
        >
          {currentIdx < STEPS.length - 1 && currentIdx >= 0 ? `${STEPS[currentIdx + 1].label} →` : ''}
        </button>
      </div>
    </div>
  );
};

export default DipBreadcrumb;
